<!-- ── New Customer Slide-in Drawer ────────────────────────────────────── -->
<app-new-customer-drawer
  #customerDrawer
  [opened]="customerDrawerOpen"
  [customerNameTaken]="customerNameTaken"
  [customerNameChecking]="customerNameChecking"
  [allCustomers]="customers"
  (closed)="closeCustomerDrawer()"
  (saved)="onNewCustomerSaved($event)"
  (nameInputChange)="onNewCustomerNameChange($event)">
</app-new-customer-drawer>

<!-- ── Page ────────────────────────────────────────────────────────────── -->
<div class="wizard-page">

  <div class="wizard-header">
    <button mat-icon-button type="button" (click)="router.navigate(['/requisitions'])"
      matTooltip="Back to Requisitions">
      <mat-icon>arrow_back</mat-icon>
    </button>
    <h2 class="wizard-title">New Requisition</h2>
  </div>

  <mat-card class="wizard-card">
    <mat-horizontal-stepper [linear]="true" #stepper>

      <!-- ══════════════════════════════════════════════════════════════ -->
      <!-- Step 1 — Requisition Details                                   -->
      <!-- ══════════════════════════════════════════════════════════════ -->
      <mat-step [stepControl]="detailsForm">
        <ng-template matStepLabel>
          <span
            matTooltip="Links this requisition to a specific Class and Customer so it can be tracked, scheduled and billed correctly"
            matTooltipPosition="below">Requisition Details</span>
        </ng-template>

        <form [formGroup]="detailsForm" class="step-form">

          <!-- Enquiry No. -->
          <mat-form-field appearance="outline" class="form-field-full">
            <mat-label>Enquiry No. *</mat-label>
            <mat-icon matPrefix>tag</mat-icon>
            <input matInput formControlName="enquiryId" autocomplete="off" placeholder="e.g. ENQ-2026-001"
              (keydown)="filterEnquiryKey($event)">
            <mat-hint *ngIf="detailsForm.get('enquiryId')?.pending">Checking…</mat-hint>
            <mat-error *ngIf="detailsForm.get('enquiryId')?.hasError('required')">
              Enquiry No. is required
            </mat-error>
            <mat-error *ngIf="detailsForm.get('enquiryId')?.hasError('pattern')">
              Only letters, numbers, hyphens and underscores allowed
            </mat-error>
            <mat-error *ngIf="detailsForm.get('enquiryId')?.hasError('duplicateEnquiry')">
              This enquiry ID is already in use
            </mat-error>
          </mat-form-field>

          <!-- Customer Type -->
          <mat-form-field appearance="outline" class="form-field-full">
            <mat-label>Customer Type *</mat-label>
            <mat-icon matPrefix>category</mat-icon>
            <input matInput [matAutocomplete]="customerTypeAuto" [(ngModel)]="customerTypeSearch"
              [ngModelOptions]="{standalone: true}" (ngModelChange)="filterCustomerTypes($event)"
              (blur)="validateCustomerTypeOnBlur()" placeholder="Select customer type…" autocomplete="off">
            <mat-autocomplete #customerTypeAuto (optionSelected)="onCustomerTypeSelected($event.option.value)">
              <mat-option *ngFor="let t of filteredCustomerTypes" [value]="t">{{ t }}</mat-option>
            </mat-autocomplete>
          </mat-form-field>
          <div class="field-error"
            *ngIf="detailsForm.get('customerType')?.invalid && detailsForm.get('customerType')?.touched">
            Customer Type is required
          </div>

          <!-- Customer Name — autocomplete only, no suffix button -->
          <mat-form-field appearance="outline" class="form-field-full">
            <mat-label>Customer Name *</mat-label>
            <mat-icon matPrefix>business</mat-icon>
            <input matInput [matAutocomplete]="customerAuto" [(ngModel)]="customerSearch"
              [ngModelOptions]="{standalone: true}" (ngModelChange)="filterCustomers($event)"
              (focus)="onCustomerInputFocus()"
              placeholder="Search customer…" autocomplete="off">
            <mat-autocomplete #customerAuto [displayWith]="displayCustomerName"
              (optionSelected)="onCustomerSelected($event.option.value)">
              <mat-option *ngFor="let c of filteredCustomers" [value]="c">{{ c.name }}</mat-option>
            </mat-autocomplete>
          </mat-form-field>
          <!-- Add new customer link sits BELOW the field, separate from the autocomplete trigger -->
          <div class="add-customer-row">
            <button type="button" class="add-customer-link" (click)="openCustomerDrawer()">
              <mat-icon>person_add</mat-icon> Add new customer
            </button>
          </div>
          <div class="field-error"
            *ngIf="detailsForm.get('customerId')?.invalid && detailsForm.get('customerId')?.touched">
            Customer is required
          </div>

          <!-- Hydrated read-only info (shown once a customer is selected) -->
          <ng-container *ngIf="selectedCustomer">
            <div class="customer-info-card">
              <!-- Row 1: Address · GST · Website -->
              <div class="ci-row">
                <div class="ci-item">
                  <span class="ci-label"><mat-icon class="ci-icon">location_on</mat-icon>Address</span>
                  <span class="ci-value">{{ selectedCustomer.address || '—' }}</span>
                </div>
                <div class="ci-item">
                  <span class="ci-label"><mat-icon class="ci-icon">receipt</mat-icon>GST No.</span>
                  <span class="ci-value">{{ selectedCustomer.gstNumber || '—' }}</span>
                </div>
                <div class="ci-item">
                  <span class="ci-label"><mat-icon class="ci-icon">language</mat-icon>Website</span>
                  <span class="ci-value">{{ selectedCustomer.website || '—' }}</span>
                </div>
              </div>
              <!-- Row 2: Contact Person · Email · Mobile -->
              <div class="ci-row">
                <div class="ci-item">
                  <span class="ci-label"><mat-icon class="ci-icon">person</mat-icon>Contact Person</span>
                  <span class="ci-value">{{ selectedCustomer.contactPerson.name || '—' }}</span>
                </div>
                <div class="ci-item">
                  <span class="ci-label"><mat-icon class="ci-icon">email</mat-icon>Email</span>
                  <span class="ci-value">{{ selectedCustomer.contactPerson.email || '—' }}</span>
                </div>
                <div class="ci-item">
                  <span class="ci-label"><mat-icon class="ci-icon">phone</mat-icon>Contact No.</span>
                  <span class="ci-value">{{ selectedCustomer.contactPerson.mobile || '—' }}</span>
                </div>
              </div>
            </div>
          </ng-container>

        </form>

        <div class="step-actions">
          <span></span>
          <button mat-flat-button color="primary" matStepperNext (click)="touchStep1()"
            [disabled]="detailsForm.invalid || detailsForm.pending">
            Next
          </button>
        </div>
      </mat-step>

      <!-- ══════════════════════════════════════════════════════════════ -->
      <!-- Step 2 — Batch Schedule                                        -->
      <!-- ══════════════════════════════════════════════════════════════ -->
      <mat-step [stepControl]="scheduleForm">
        <ng-template matStepLabel>
          <span
            matTooltip="Session slots define when and where training happens — accurate dates ensure correct VM provisioning windows"
            matTooltipPosition="below">Batch Schedule</span>
        </ng-template>

        <form [formGroup]="scheduleForm" class="step-form">

          <!-- Batch Title -->
          <mat-form-field appearance="outline" class="form-field-full">
            <mat-label>Batch Title *</mat-label>
            <mat-icon matPrefix>label</mat-icon>
            <input matInput formControlName="batchTitle" placeholder="e.g. Morning Batch — March 2026">
            <mat-error *ngIf="scheduleForm.get('batchTitle')?.hasError('required')">
              Batch title is required
            </mat-error>
            <mat-error *ngIf="scheduleForm.get('batchTitle')?.hasError('pattern')">
              Only letters, numbers, spaces, hyphens, underscores and dots are allowed
            </mat-error>
          </mat-form-field>

          <!-- Trainer (optional) -->
          <mat-form-field appearance="outline" class="form-field-full">
            <mat-label>Trainer (optional)</mat-label>
            <mat-icon matPrefix>person_pin</mat-icon>
            <input matInput [matAutocomplete]="trainerAuto" [(ngModel)]="trainerSearch"
              [ngModelOptions]="{standalone: true}" (ngModelChange)="filterTrainers($event)"
              (blur)="validateTrainerOnBlur()"
              [placeholder]="trainers.length === 0 ? 'No trainers available' : 'Search trainer…'"
              [disabled]="trainers.length === 0" autocomplete="off">
            <button *ngIf="selectedTrainer" matSuffix mat-icon-button type="button" (click)="clearTrainer()"
              matTooltip="Clear trainer">
              <mat-icon>close</mat-icon>
            </button>
            <mat-autocomplete #trainerAuto [displayWith]="displayTrainerName"
              (optionSelected)="onTrainerSelected($event.option.value)">
              <mat-option *ngFor="let t of filteredTrainers" [value]="t">{{ t.name }}</mat-option>
            </mat-autocomplete>
          </mat-form-field>
          <p class="trainer-hint" *ngIf="trainers.length === 0">No users with trainer role found — trainer assignment
            can be done later.</p>

          <!-- Session Slots (Date Ranges) -->
          <div class="date-ranges-section">

            <div class="section-sub-heading-row">
              <span class="section-sub-heading">Session Slots *</span>
              <button mat-stroked-button type="button" class="add-range-btn" (click)="addDateRange()">
                <mat-icon>add</mat-icon> Add Session Slot
              </button>
            </div>

            <mat-accordion class="slot-accordion" [multi]="true" formArrayName="dateRanges">
              <mat-expansion-panel *ngFor="let ctrl of dateRanges.controls; let i = index" [formGroupName]="i"
                class="slot-panel" [class.slot-panel--overlap]="isSlotInOverlap(i)" [expanded]="expandedSlotIndex === i"
                (opened)="expandedSlotIndex = i">

                <mat-expansion-panel-header>
                  <mat-panel-title class="slot-panel-title">
                    <mat-icon class="slot-panel-icon">calendar_today</mat-icon>
                    <input class="slot-title-input" formControlName="slotTitle" [placeholder]="'Slot ' + (i + 1)"
                      (click)="$event.stopPropagation()" (keydown.enter)="$event.preventDefault()"
                      (keydown.space)="$event.stopPropagation()" (keydown)="filterSlotTitleKey($event)"
                      (paste)="filterSlotTitlePaste($event, i)" (blur)="trimSlotTitle(i)" autocomplete="off"
                      maxlength="40" />
                    <div *ngIf="isSlotTitleInvalid(i)" class="slot-title-error">
                      Only letters, numbers, spaces, hyphens, underscores and dots are allowed
                    </div>
                    <span class="slot-panel-header-summary" *ngIf="ctrl.get('startDate')?.value">
                      &mdash; {{ ctrl.get('startDate')?.value | date:'d MMM' }}
                      to {{ ctrl.get('endDate')?.value | date:'d MMM yyyy' }}
                      <span class="slot-header-times" *ngIf="ctrl.get('startTime')?.value">
                        &middot; {{ formatTime12h(ctrl.get('startTime')?.value) }}&ndash;{{
                        formatTime12h(ctrl.get('endTime')?.value) }}
                      </span>
                    </span>
                  </mat-panel-title>
                  <mat-panel-description>
                    <button mat-icon-button type="button" color="warn"
                      (click)="$event.stopPropagation(); removeDateRange(i)" [disabled]="dateRanges.length === 1"
                      matTooltip="Remove slot">
                      <mat-icon>delete_outline</mat-icon>
                    </button>
                  </mat-panel-description>
                </mat-expansion-panel-header>

                <div class="slot-panel-body">
                  <div class="slot-title-error" *ngIf="isSlotTitleDuplicate(i)">
                    <mat-icon>error_outline</mat-icon> Slot name already used &mdash; each slot must have a unique name.
                  </div>
                  <!-- Full-width date range picker -->
                  <mat-form-field appearance="outline" class="date-range-field">
                    <mat-label>Date Range *</mat-label>
                    <mat-date-range-input [rangePicker]="rangePicker" [min]="today" (click)="rangePicker.open()">
                      <input matStartDate formControlName="startDate" placeholder="Start date" readonly>
                      <input matEndDate formControlName="endDate" placeholder="End date" readonly>
                    </mat-date-range-input>
                    <mat-datepicker-toggle matIconSuffix [for]="rangePicker"></mat-datepicker-toggle>
                    <mat-date-range-picker #rangePicker></mat-date-range-picker>
                    <mat-error
                      *ngIf="ctrl.get('startDate')?.hasError('required') || ctrl.get('endDate')?.hasError('required')">
                      Start and end dates are required
                    </mat-error>
                  </mat-form-field>

                  <!-- Times on the row below -->
                  <div class="times-row">
                    <app-time-picker formControlName="startTime" label="Start Time"></app-time-picker>
                    <app-time-picker formControlName="endTime" label="End Time"></app-time-picker>
                  </div>
                </div>

              </mat-expansion-panel>
            </mat-accordion>

            <div class="slot-overlap-error" *ngIf="dateRanges.hasError('slotOverlap')">
              <mat-icon>warning_amber</mat-icon>
              <span>Slot {{ dateRanges.errors?.['slotOverlap']?.slotA }} and Slot {{
                dateRanges.errors?.['slotOverlap']?.slotB }} have overlapping dates &mdash; adjust one of the
                ranges.</span>
            </div>

          </div>

        </form>

        <div class="step-actions">
          <button mat-button matStepperPrevious>Back</button>
          <button mat-flat-button color="primary" matStepperNext (click)="touchStep2()"
            [disabled]="scheduleForm.invalid || hasDuplicateSlotTitles()">
            Next
          </button>
        </div>
      </mat-step>

      <!-- ══════════════════════════════════════════════════════════════ -->
      <!-- Step 3 — Additional Information                                -->
      <!-- ══════════════════════════════════════════════════════════════ -->
      <mat-step [stepControl]="additionalForm">
        <ng-template matStepLabel>
          <span
            matTooltip="Extra context helps coordinators and trainers action the requisition without needing to follow up separately"
            matTooltipPosition="below">Additional Info</span>
        </ng-template>

        <form [formGroup]="additionalForm" class="step-form">
          <mat-form-field appearance="outline" class="form-field-full">
            <mat-label>Special Requirements</mat-label>
            <mat-icon matPrefix>report_problem</mat-icon>
            <textarea matInput formControlName="specialRequirements" rows="4"
              placeholder="Any special equipment, accessibility needs, etc."></textarea>
          </mat-form-field>

          <mat-form-field appearance="outline" class="form-field-full">
            <mat-label>Final Comments</mat-label>
            <mat-icon matPrefix>comment</mat-icon>
            <textarea matInput formControlName="finalComments" rows="4"
              placeholder="Any final notes or comments…"></textarea>
          </mat-form-field>
        </form>

        <div class="step-actions">
          <button mat-button matStepperPrevious>Back</button>
          <button mat-flat-button color="primary" matStepperNext>Next</button>
        </div>
      </mat-step>

      <!-- ══════════════════════════════════════════════════════════════ -->
      <!-- Step 4 — Review & Forward                                      -->
      <!-- ══════════════════════════════════════════════════════════════ -->
      <mat-step [stepControl]="forwardForm">
        <ng-template matStepLabel>
          <span
            matTooltip="Once forwarded, the requisition enters the processing queue — verify everything is accurate before submitting"
            matTooltipPosition="below">Review &amp; Forward</span>
        </ng-template>

        <!-- ── Accordion review panels — collapsed by default, inline summary when closed ── -->
        <mat-accordion class="review-accordion" [multi]="false">

          <!-- Panel A: Requisition Details -->
          <mat-expansion-panel #panelA class="review-panel" [expanded]="false">
            <mat-expansion-panel-header>
              <mat-panel-title class="review-panel-title">
                <mat-icon class="review-panel-icon">list_alt</mat-icon>
                Requisition Details
              </mat-panel-title>
              <mat-panel-description *ngIf="!panelA.expanded">
                <div class="review-panel-summary">
                  <span class="rps-item" *ngIf="detailsForm.value.enquiryId"><mat-icon class="rps-icon">tag</mat-icon>{{
                    detailsForm.value.enquiryId }}</span>
                  <span class="rps-item" *ngIf="selectedCustomer"><mat-icon class="rps-icon">business</mat-icon>{{
                    selectedCustomer!.name }}</span>
                  <span class="rps-item" *ngIf="detailsForm.value.customerType"><mat-icon
                      class="rps-icon">category</mat-icon>{{ detailsForm.value.customerType }}</span>
                </div>
              </mat-panel-description>
            </mat-expansion-panel-header>
            <div class="review-grid">
              <span class="review-label"><mat-icon class="rl-icon">tag</mat-icon>Enquiry No.</span>
              <span class="review-value" [matTooltip]="detailsForm.value.enquiryId">{{ detailsForm.value.enquiryId ||
                '—' }}</span>
              <span class="review-label"><mat-icon class="rl-icon">category</mat-icon>Customer Type</span>
              <span class="review-value" [matTooltip]="detailsForm.value.customerType">{{ detailsForm.value.customerType
                || '—' }}</span>

              <span class="review-label"><mat-icon class="rl-icon">business</mat-icon>Customer</span>
              <span class="review-value" [matTooltip]="selectedCustomer?.name">{{ selectedCustomer?.name || '—'
                }}</span>
              <span class="review-label"><mat-icon class="rl-icon">receipt_long</mat-icon>GST No.</span>
              <span class="review-value">{{ selectedCustomer?.gstNumber || '—' }}</span>

              <span class="review-label"><mat-icon class="rl-icon">person</mat-icon>Contact Person</span>
              <span class="review-value" [matTooltip]="selectedCustomer?.contactPerson?.name">{{
                selectedCustomer?.contactPerson?.name || '—' }}</span>
              <span class="review-label"><mat-icon class="rl-icon">phone</mat-icon>Contact No.</span>
              <span class="review-value">{{ selectedCustomer?.contactPerson?.mobile || '—' }}</span>

              <span class="review-label"><mat-icon class="rl-icon">alternate_email</mat-icon>Email</span>
              <span class="review-value" [matTooltip]="selectedCustomer?.contactPerson?.email"
                style="grid-column: span 3">{{ selectedCustomer?.contactPerson?.email || '—' }}</span>
            </div>
          </mat-expansion-panel>

          <!-- Panel B: Batch Schedule -->
          <mat-expansion-panel #panelB class="review-panel" [expanded]="false">
            <mat-expansion-panel-header>
              <mat-panel-title class="review-panel-title">
                <mat-icon class="review-panel-icon">calendar_month</mat-icon>
                Batch Schedule
              </mat-panel-title>
              <mat-panel-description *ngIf="!panelB.expanded">
                <div class="review-panel-summary">
                  <span class="rps-item" *ngIf="scheduleForm.value.batchTitle"><mat-icon
                      class="rps-icon">label</mat-icon>{{ scheduleForm.value.batchTitle }}</span>
                  <span class="rps-item" *ngIf="selectedTrainer"><mat-icon class="rps-icon">person_pin</mat-icon>{{
                    selectedTrainer!.name }}</span>
                  <span class="rps-item"><mat-icon class="rps-icon">calendar_today</mat-icon>{{ reviewDateRanges.length
                    }} slot{{ reviewDateRanges.length !== 1 ? 's' : '' }}</span>
                </div>
              </mat-panel-description>
            </mat-expansion-panel-header>
            <div class="review-grid">
              <span class="review-label"><mat-icon class="rl-icon">label</mat-icon>Batch Title</span>
              <span class="review-value" [matTooltip]="scheduleForm.value.batchTitle">{{ scheduleForm.value.batchTitle
                || '—' }}</span>
              <span class="review-label"><mat-icon class="rl-icon">person_pin</mat-icon>Trainer</span>
              <span class="review-value" [matTooltip]="selectedTrainer?.name">{{ selectedTrainer?.name || '— None —'
                }}</span>
            </div>
            <div class="slot-review-list" *ngIf="reviewDateRanges.length > 0">
              <div *ngFor="let r of reviewDateRanges; let i = index" class="slot-review-row">
                <mat-icon>event</mat-icon>
                <span>{{ r.slotTitle || ('Slot ' + (i + 1)) }}: {{ r.startDate | date:'d MMM' }} – {{ r.endDate |
                  date:'d MMM yyyy' }}</span>
                <span class="slot-time">{{ formatTime12h(r.startTime) }} – {{ formatTime12h(r.endTime) }}</span>
              </div>
            </div>
          </mat-expansion-panel>

          <!-- Panel C: Additional Information -->
          <mat-expansion-panel #panelC class="review-panel" [expanded]="false">
            <mat-expansion-panel-header>
              <mat-panel-title class="review-panel-title">
                <mat-icon class="review-panel-icon">sticky_note_2</mat-icon>
                Additional Information
              </mat-panel-title>
              <mat-panel-description *ngIf="!panelC.expanded">
                <div class="review-panel-summary">
                  <span class="rps-item" *ngIf="additionalForm.value.specialRequirements"><mat-icon
                      class="rps-icon">report_problem</mat-icon>Special requirements</span>
                  <span class="rps-item"
                    *ngIf="!additionalForm.value.specialRequirements && !additionalForm.value.finalComments">No
                    additional info</span>
                </div>
              </mat-panel-description>
            </mat-expansion-panel-header>
            <div class="review-grid">
              <span class="review-label"><mat-icon class="rl-icon">report_problem</mat-icon>Special Requirements</span>
              <span class="review-value" [matTooltip]="additionalForm.value.specialRequirements || ''"
                matTooltipShowDelay="300">
                {{ additionalForm.value.specialRequirements || '—' }}
              </span>
              <span class="review-label"><mat-icon class="rl-icon">comment</mat-icon>Final Comments</span>
              <span class="review-value" [matTooltip]="additionalForm.value.finalComments || ''"
                matTooltipShowDelay="300">
                {{ additionalForm.value.finalComments || '—' }}
              </span>
            </div>
          </mat-expansion-panel>

        </mat-accordion>

        <!-- ── Forward form — separate from review, always visible ── -->
        <div class="forward-section">
          <div class="forward-section-header">
            <span class="forward-section-title">
              <mat-icon>forward</mat-icon>
              Forward To
              <span class="activity-badge">→ {{ FORWARD_ACTIVITY_CODE }}</span>
            </span>
          </div>

          <form [formGroup]="forwardForm" class="forward-form">

            <mat-form-field appearance="outline" class="form-field-full">
              <mat-label>Team *</mat-label>
              <mat-icon matPrefix>groups</mat-icon>
              <mat-select formControlName="team">
                <mat-option *ngFor="let t of teams" [value]="t.team">{{ t.team }}</mat-option>
              </mat-select>
              <mat-error *ngIf="forwardForm.get('team')?.hasError('required')">
                Team is required
              </mat-error>
            </mat-form-field>

            <mat-form-field appearance="outline" class="form-field-full">
              <mat-label>User *</mat-label>
              <mat-icon matPrefix>person</mat-icon>
              <mat-select formControlName="user">
                <mat-option *ngFor="let u of users" [value]="u.name">{{ u.name }}</mat-option>
              </mat-select>
              <mat-error *ngIf="forwardForm.get('user')?.hasError('required')">
                User is required
              </mat-error>
            </mat-form-field>

            <mat-form-field appearance="outline" class="form-field-full">
              <mat-label>Remarks *</mat-label>
              <mat-icon matPrefix>comment</mat-icon>
              <textarea matInput formControlName="remarks" rows="3" placeholder="Forwarding notes…"></textarea>
              <mat-error *ngIf="forwardForm.get('remarks')?.hasError('required')">
                Remarks are required
              </mat-error>
            </mat-form-field>

          </form>
        </div>

        <div class="step-actions">
          <button mat-button matStepperPrevious>Back</button>
          <button mat-flat-button color="primary" (click)="submit()" [disabled]="forwardForm.invalid || isSubmitting">
            <mat-progress-spinner *ngIf="isSubmitting" mode="indeterminate" diameter="18" class="btn-spinner">
            </mat-progress-spinner>
            Create &amp; Forward
          </button>
        </div>

      </mat-step>

    </mat-horizontal-stepper>
  </mat-card>
</div>